import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiPost } from '@/lib/api';

/**
 * Content Authentication Callback Page
 *
 * Handles OAuth callback from OptiPrime.
 * Exchanges authorization code for JWT session cookie.
 */
export default function ContentPass(): JSX.Element {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasRun.current) return;
    hasRun.current = true;

    async function handleCallback(): Promise<void> {
      try {
        // 1. Get OAuth callback parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
          throw new Error('Missing OAuth parameters');
        }

        // 2. Validate state (CSRF protection)
        const storedState = sessionStorage.getItem('content_oauth_state');
        if (state !== storedState) {
          throw new Error('Invalid state parameter - possible CSRF attack');
        }

        // 3. Retrieve stored PKCE data
        const codeVerifier = sessionStorage.getItem('content_code_verifier');
        const projectId = sessionStorage.getItem('content_project_id');
        const returnUrl = sessionStorage.getItem('content_return_url');

        if (!codeVerifier || !projectId || !returnUrl) {
          throw new Error('Missing session data - please try again');
        }

        // 4. Exchange code with backend (no auth required for this endpoint)
        const response = await apiPost(
          '/api/auth/content-pass',
          {
            code,
            codeVerifier,
            projectId,
            returnUrl
          },
          false,
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Authentication failed' }));
          throw new Error(errorData.message || 'Authentication failed');
        }

        const data = await response.json();
        if (!data.success || !data.token) {
          throw new Error('Authentication failed - missing token');
        }

        // 5. Clear session storage
        sessionStorage.removeItem('content_code_verifier');
        sessionStorage.removeItem('content_oauth_state');
        sessionStorage.removeItem('content_project_id');
        sessionStorage.removeItem('content_return_url');

        // 6. Submit token via POST form (secure - not in URL)
        // This allows CloudFlare Worker to set cookie for any domain (including custom domains)
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = returnUrl;
        form.style.display = 'none';

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'auth_token';
        input.value = data.token;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
      } catch (err) {
        // Ignore abort errors (happens during cleanup/unmount)
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Content auth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    }

    handleCallback();
  }, [searchParams]);

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Failed</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}
